<template>
  <div id="TwoFactorLogin">
    <div class="container login">
      <div class="row page-content">
        <div
          class="col-md-7 login-bg img-part px-0"
          :class="{
            'bg-brand-secondary': skin > 0,
            'bg-grad-image-purple': skin === 0,
          }"
        >
          <div class="login-bg">
            <div class="heart-placeholder">
              <img
                v-if="hardcoded.spectrum_heart"
                class="heart-img"
                :src="hardcoded.spectrum_heart"
              />
              <img class="heart-img" src="/img/default-asset.svg" v-else />
            </div>
            <div class="forgot-pass-image"></div>
          </div>
        </div>
        <div class="col-md-5 form min-height">
          <div v-if="hardcoded.login_logos" class="logos d-flex pt-5">
            <div
              v-for="(logo, i) in hardcoded.login_logos"
              :key="i"
              class="d-flex logo px-1"
            >
              <img :src="logo" />
            </div>
          </div>
          <div class="form-content">
            <div
              :class="[
                ($store.getters.client.client_logo
                  .toLowerCase()
                  .includes('spectrum-life-logo') ||
                  $store.getters.client.client_logo
                    .toLowerCase()
                    .includes('lloydwhyte')) &&
                skin === 15
                  ? 'lw-form-logo'
                  : 'form-logo',
              ]"
            >
              <img
                :src="getLogo"
                onerror="javascript:this.src='/img/logo-spectrum-life.svg'"
              />
            </div>
            <h2 class="form-title">
              Enter 6 digit verification code
            </h2>
            <b-form-group
              id="input-group-1"
              :label="'Enter your verification code'"
              label-for="inputEmail"
            >
              <b-form-input
                type="text"
                v-model="code"
                :state="code.length === 6"
                id="inputCode"
                max="6"
                required
                @keydown="editCode"
              ></b-form-input>
            </b-form-group>
            <div
              col-sm-12
              class="submit-form d-flex align-items-center justify-content-center"
            >
              <b-button
                id="two-factor-continue"
                class="cta-button py-3 "
                type="button"
                @click="submitForm"
                :disabled="!verifyToken || code.length !== 6 || isSubmitting"
                >Submit</b-button
              >
            </div>
            <div class="col-12 new-here">
              <a
                id="two-factor-resend"
                class="text-link"
                :class="{ disabled: !canResend }"
                @click="resendCode"
                >{{ resendLabel }}</a
              >
            </div>
            <div v-if="resendFeedbackMessage" class="col-12 resend-feedback">
              {{ resendFeedbackMessage }}
            </div>
            <div class="col-12 new-here">
              <a
                id="two-factor-back-to-login"
                class="text-link"
                @click="leaveTwoFactor"
                >{{ componentText.back }}</a
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  buildTwoFactorRoute,
  EMPTY_TWO_FACTOR_CHALLENGE,
  MFA_SESSION_EXPIRED_MESSAGE,
  resendCodeLabel,
  resendFailureCooldownSeconds,
  resolveActiveChallenge,
  resolveVerifyToken,
  TWO_FACTOR_RESEND_COOLDOWN_SECONDS,
  TWO_FACTOR_RESEND_SUCCESS_MESSAGE,
} from "@/utils/twoFactor/twoFactor";

export default {
  name: "TwoFactorLogin",
  data() {
    return {
      code: "",
      isSubmitting: false,
      isResending: false,
      resendFeedbackMessage: "",
      resendCooldownSeconds: 0,
      resendCooldownTimer: null,
    };
  },
  mounted() {
    if (this.challenge.initialError) {
      this.$store.dispatch("setWarning", this.challenge.initialError);
    }
    if (!this.challenge.skipInitialCooldown) {
      this.startResendCooldown(TWO_FACTOR_RESEND_COOLDOWN_SECONDS);
    }
  },
  beforeDestroy() {
    this.clearResendCooldown();
  },
  methods: {
    editCode(evt) {
      if (isNaN(evt.key) && evt.key !== "Backspace") {
        evt.preventDefault();
      }
      if (evt.key !== "Backspace" && this.code.length > 5) {
        evt.preventDefault();
      }
    },
    clearResendCooldown() {
      if (this.resendCooldownTimer !== null) {
        clearInterval(this.resendCooldownTimer);
        this.resendCooldownTimer = null;
      }
    },
    startResendCooldown(seconds) {
      this.clearResendCooldown();
      if (seconds <= 0) {
        this.resendCooldownSeconds = 0;
        return;
      }
      this.resendCooldownSeconds = seconds;
      this.resendCooldownTimer = setInterval(() => {
        this.resendCooldownSeconds -= 1;
        if (this.resendCooldownSeconds <= 0) {
          this.clearResendCooldown();
        }
      }, 1000);
    },
    async leaveTwoFactor() {
      await this.$store.dispatch("clearTwoFactorChallenge");
      await this.$store.dispatch("acceptWarning");
      this.resendFeedbackMessage = "";
      this.$emit("sign-in");
      await this.$router.replace("/login").catch(() => undefined);
    },
    async applyVerifyToken(token) {
      if (!token) {
        return;
      }
      const nextChallenge = {
        ...this.challenge,
        temporaryToken: token,
        phoneLast4: this.challenge.phoneLast4 || this.phoneLast4,
        initialError: "",
      };
      await this.$store.dispatch("setTwoFactorChallenge", nextChallenge);
      await this.$router
        .replace(buildTwoFactorRoute(nextChallenge))
        .catch(() => undefined);
    },
    async submitForm() {
      if (!this.verifyToken || this.code.length !== 6 || this.isSubmitting) {
        return;
      }
      this.resendFeedbackMessage = "";
      this.isSubmitting = true;
      try {
        const v3Ready = await this.$store.dispatch("twoFactorSignIn", {
          router: this.$router,
          payload: {
            token: this.verifyToken,
            code: this.code,
            platform: "web",
          },
        });
        if (v3Ready === false) {
          this.$emit("not-v3");
        }
      } finally {
        this.isSubmitting = false;
      }
    },
    async resendCode() {
      if (!this.canResend) {
        return;
      }
      this.isResending = true;
      try {
        if (this.verifyToken) {
          const result = await this.$store.dispatch(
            "twoFactorResend",
            this.verifyToken,
          );
          if (!result) {
            return;
          }
          if (result.success) {
            this.code = "";
            this.resendFeedbackMessage = TWO_FACTOR_RESEND_SUCCESS_MESSAGE;
            await this.applyVerifyToken(result.token);
            this.startResendCooldown(TWO_FACTOR_RESEND_COOLDOWN_SECONDS);
            return;
          }
          this.resendFeedbackMessage = "";
          this.startResendCooldown(
            resendFailureCooldownSeconds(result.waitSeconds),
          );
          return;
        }
        if (this.challenge.oathToken && this.challenge.phone) {
          const sentToken = await this.$store.dispatch("twoFactorSend", {
            token: this.challenge.oathToken,
            phone: this.challenge.phone,
          });
          if (sentToken) {
            this.code = "";
            this.resendFeedbackMessage = TWO_FACTOR_RESEND_SUCCESS_MESSAGE;
            await this.applyVerifyToken(sentToken);
            this.startResendCooldown(TWO_FACTOR_RESEND_COOLDOWN_SECONDS);
          } else {
            this.resendFeedbackMessage = "";
            this.startResendCooldown(TWO_FACTOR_RESEND_COOLDOWN_SECONDS);
          }
          return;
        }
        this.resendFeedbackMessage = "";
        await this.$store.dispatch("setWarning", MFA_SESSION_EXPIRED_MESSAGE);
      } finally {
        this.isResending = false;
      }
    },
  },
  computed: {
    ...mapGetters(["hardcoded", "skin", "twoFactorChallenge"]),
    challenge() {
      return resolveActiveChallenge(
        this.$route.query.oauth,
        this.twoFactorChallenge || EMPTY_TWO_FACTOR_CHALLENGE,
      );
    },
    verifyToken() {
      return resolveVerifyToken(this.$route.query.oauth, this.challenge);
    },
    phoneLast4() {
      const last4 = this.$route.query.phone_last4;
      if (typeof last4 === "string" && last4) {
        return last4;
      }
      return this.challenge.phoneLast4 || "";
    },
    canResend() {
      return (
        !this.isResending &&
        !this.isSubmitting &&
        this.resendCooldownSeconds === 0
      );
    },
    resendLabel() {
      return resendCodeLabel(this.resendCooldownSeconds, this.isResending);
    },
    getLogo() {
      if (this.hardcoded && this.hardcoded.logo) {
        return this.hardcoded.logo;
      } else return this.$store.getters.client.client_logo;
    },
    componentText() {
      return this.getText.notLoggedIn.forgotPassword;
    },
  },
};
</script>
<style lang="less">
#TwoFactorLogin {
  #inputCode {
    text-align: center;
  }
  .text-link.disabled {
    cursor: default;
    opacity: 0.6;
    pointer-events: none;
  }
  .resend-feedback {
    color: #1f6f43;
    text-align: center;
    margin-top: 8px;
  }
}
</style>
